import { useState, useEffect, useRef, useCallback } from 'react';
import type { RefObject } from 'react';
import {
  transcodeToPlayableMp4,
  type TranscodeProgress,
  type VideoSourceLoader,
} from './videoTranscode';

// Edge has no software AV1 decoder and needs a Store extension for HEVC, so a
// file Chrome plays fine can decode audio only - a silent black frame with no
// error event. When that happens we transcode to H.264 in the page.

// Seconds of playback with zero decoded frames before we call the track dead
const NO_FRAMES_THRESHOLD = 1;

export interface VideoDecodeFallbackHandlers {
  onLoadedData: () => void;
  onPlaying: () => void;
  onTimeUpdate: () => void;
  onError: () => void;
}

export interface VideoDecodeFallback {
  // What to put in <video src>: the transcoded blob once ready, else `url`
  src: string;
  transcodeProgress: TranscodeProgress | null;
  transcodeFailed: boolean;
  handlers: VideoDecodeFallbackHandlers;
}

function decodedFrameCount(el: HTMLVideoElement): number | null
{
  if (typeof el.getVideoPlaybackQuality === 'function')
  {
    return el.getVideoPlaybackQuality().totalVideoFrames;
  }

  const legacy = (el as HTMLVideoElement & { webkitDecodedFrameCount?: number }).webkitDecodedFrameCount;
  return typeof legacy === 'number' ? legacy : null;
}

export function useVideoDecodeFallback(
  url: string,
  videoRef: RefObject<HTMLVideoElement | null>,
  // Reads the file through the API; the storage bucket does not allow CORS
  loadSource?: VideoSourceLoader,
): VideoDecodeFallback
{
  // Callers pass a fresh closure each render; only the latest one matters
  const loadSourceRef = useRef(loadSource);
  loadSourceRef.current = loadSource;

  const [fallbackUrl, setFallbackUrl] = useState<string | null>(null);
  const [transcodeProgress, setTranscodeProgress] = useState<TranscodeProgress | null>(null);
  const [transcodeFailed, setTranscodeFailed] = useState(false);

  const transcodeAbortRef = useRef<AbortController | null>(null);
  const attemptedUrlRef = useRef<string | null>(null);

  // A different file may well be natively decodable
  useEffect(() =>
  {
    transcodeAbortRef.current?.abort();
    attemptedUrlRef.current = null;
    setTranscodeFailed(false);
    setTranscodeProgress(null);
    setFallbackUrl(prev =>
    {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  }, [url]);

  // Release the blob and stop any in-flight work on unmount
  useEffect(() => () =>
  {
    transcodeAbortRef.current?.abort();
    setFallbackUrl(prev =>
    {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  }, []);

  const startTranscode = useCallback(async (sourceUrl: string) =>
  {
    // Only one attempt per source
    if (!sourceUrl || attemptedUrlRef.current === sourceUrl) return;
    attemptedUrlRef.current = sourceUrl;

    // Audio over a black frame only confuses; the converted copy autoplays
    videoRef.current?.pause();

    const controller = new AbortController();
    transcodeAbortRef.current = controller;

    try
    {
      setTranscodeProgress({ ratio: null, stage: 'loading-core' });

      const playableUrl = await transcodeToPlayableMp4(
        sourceUrl,
        setTranscodeProgress,
        controller.signal,
        loadSourceRef.current,
      );

      if (controller.signal.aborted)
      {
        URL.revokeObjectURL(playableUrl);
        return;
      }

      setFallbackUrl(playableUrl);
    }
    catch (err)
    {
      if ((err as DOMException)?.name !== 'AbortError' && !controller.signal.aborted)
      {
        console.error('VideoModal: in-browser transcode failed', err);
        setTranscodeFailed(true);
      }
    }
    finally
    {
      if (transcodeAbortRef.current === controller)
      {
        setTranscodeProgress(null);
      }
    }
  }, [videoRef]);

  // Dimensions of 0 once data has arrived mean the video track did not decode.
  // No error event fires in that case - the element reports itself as playing.
  const checkVideoTrack = useCallback((): void =>
  {
    const el = videoRef.current;
    if (!el || fallbackUrl) return;

    if (el.readyState >= 2 && (el.videoWidth === 0 || el.videoHeight === 0))
    {
      startTranscode(url);
    }
  }, [fallbackUrl, startTranscode, url, videoRef]);

  // Some builds report the container's dimensions even though no frame ever
  // decodes, so also watch the decoded frame counter while time advances.
  const checkDecodedFrames = useCallback((): void =>
  {
    const el = videoRef.current;
    if (!el || fallbackUrl || el.currentTime < NO_FRAMES_THRESHOLD) return;

    if (decodedFrameCount(el) === 0)
    {
      startTranscode(url);
    }
  }, [fallbackUrl, startTranscode, url, videoRef]);

  const handleError = useCallback((): void =>
  {
    // An error on our own blob means the transcode output is unusable too
    if (fallbackUrl)
    {
      setTranscodeFailed(true);
      return;
    }
    startTranscode(url);
  }, [fallbackUrl, startTranscode, url]);

  return {
    src: fallbackUrl ?? url,
    transcodeProgress,
    transcodeFailed,
    handlers: {
      onLoadedData: checkVideoTrack,
      onPlaying: checkVideoTrack,
      onTimeUpdate: checkDecodedFrames,
      onError: handleError,
    },
  };
}
